import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { Dialog } from '@base-ui/react/dialog'
import { cashTransferSchema, type CashTransferFormData } from '@/validators/cash'
import { useCreateTransfer } from '@/hooks/useCashTransactions'
import { useCashAccounts } from '@/hooks/useCashAccounts'
import { MoneyInput } from '@/components/form/money-input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

function apiError(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? fallback
  )
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

interface TransferModalProps {
  open: boolean
  onClose: () => void
}

export function TransferModal({ open, onClose }: TransferModalProps) {
  const createMutation = useCreateTransfer()
  const { data: accountsData } = useCashAccounts({ active: true, perPage: 100 })
  const accounts = accountsData?.data ?? []

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<CashTransferFormData>({
    resolver: zodResolver(cashTransferSchema),
    defaultValues: {
      fromAccountId: '',
      toAccountId: '',
      amount: '' as unknown as number,
      date: todayISO(),
      description: 'Transferência entre contas',
    },
  })

  useEffect(() => {
    if (open) {
      reset({
        fromAccountId: '',
        toAccountId: '',
        amount: '' as unknown as number,
        date: todayISO(),
        description: 'Transferência entre contas',
      })
    }
  }, [open, reset])

  async function onSubmit(data: CashTransferFormData) {
    try {
      await createMutation.mutateAsync({ ...data, date: new Date(data.date).toISOString() })
      toast.success('Transferência registrada!')
      onClose()
    } catch (err) {
      toast.error(apiError(err, 'Erro ao registrar transferência'))
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            Transferência entre contas
          </Dialog.Title>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tr-from">Conta de origem *</Label>
              <Controller
                name="fromAccountId"
                control={control}
                render={({ field }) => (
                  <select id="tr-from" value={field.value} onChange={field.onChange} className={selectClass}>
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.fromAccountId && (
                <p className="text-xs text-destructive">{errors.fromAccountId.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tr-to">Conta de destino *</Label>
              <Controller
                name="toAccountId"
                control={control}
                render={({ field }) => (
                  <select id="tr-to" value={field.value} onChange={field.onChange} className={selectClass}>
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.toAccountId && (
                <p className="text-xs text-destructive">{errors.toAccountId.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="tr-amount">Valor *</Label>
                <MoneyInput id="tr-amount" error={!!errors.amount} {...register('amount')} />
                {errors.amount && <p className="text-xs text-destructive">{errors.amount.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tr-date">Data *</Label>
                <Input id="tr-date" type="date" aria-invalid={!!errors.date} {...register('date')} />
                {errors.date && <p className="text-xs text-destructive">{errors.date.message}</p>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tr-description">Descrição *</Label>
              <Input id="tr-description" aria-invalid={!!errors.description} {...register('description')} />
              {errors.description && (
                <p className="text-xs text-destructive">{errors.description.message}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={createMutation.isPending}>
                Cancelar
              </Button>
              <LoadingButton type="submit" loading={createMutation.isPending} loadingText="Transferindo…">
                Transferir
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
